import { useEffect, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { Select } from 'antd';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { FilterPanel } from './FilterPanel';

type NullChoice = 'null' | 'nnull';

type NullFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  nullLabel?: string;
  notNullLabel?: string;
};

/**
 * "Set / not set" column filter, applied as `field[null]` or `field[nnull]`.
 *
 * Refine keys filters by field *and* operator, so switching sides has to clear
 * the opposite operator explicitly.
 */
export const NullFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  nullLabel,
  notNullLabel,
}: NullFilterProps) => {
  const t = useTranslate();
  const current: NullChoice | undefined = getFilterValue<boolean>(
    filters,
    field,
    'null'
  )
    ? 'null'
    : getFilterValue<boolean>(filters, field, 'nnull')
      ? 'nnull'
      : undefined;
  const [pending, setPending] = useState<NullChoice | undefined>(current);

  useEffect(() => setPending(current), [current]);

  const apply = (choice: NullChoice | undefined) =>
    setFilters(
      [
        {
          field,
          operator: 'null',
          value: choice === 'null' ? true : undefined,
        },
        {
          field,
          operator: 'nnull',
          value: choice === 'nnull' ? true : undefined,
        },
      ],
      'merge'
    );

  const handleFilter = () => {
    apply(pending);
    confirm();
  };

  const handleReset = () => {
    setPending(undefined);
    apply(undefined);
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <Select
        value={pending}
        onChange={(value?: NullChoice) => setPending(value)}
        style={{ minWidth: 160 }}
        allowClear
        options={[
          {
            label: notNullLabel ?? t('base.filter.not_null', 'Ustawione'),
            value: 'nnull',
          },
          {
            label: nullLabel ?? t('base.filter.null', 'Nieustawione'),
            value: 'null',
          },
        ]}
      />
    </FilterPanel>
  );
};
